import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select';
import { withDarkStyles } from './utils/selectStyles';
import { matchesAllTokens } from './utils/selectFilter';
import { nextDayCorrectedEnd } from './utils/durationRule';
import { initialSendToCapLegal } from './utils/sendToCapLegal';
import { billingTypeForMatter } from './utils/billingForMatter';
import { worktypeMenuMessage } from './utils/worktypeLoad';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './components/ConfirmModal';
import { API } from './api/api';

// Helper functions for time formatting
const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

// Parse flexible time input - supports both decimal (0,1) and HH:MM (1:30) formats
const parseFlexibleTimeToDecimalHours = (timeString) => {
  if (!timeString || !timeString.trim()) return 0;
  
  const trimmed = timeString.trim();
  
  // Check if it's decimal format (contains comma)
  if (trimmed.includes(',')) {
    const decimalValue = parseFloat(trimmed.replace(',', '.'));
    if (isNaN(decimalValue)) throw new Error('Invalid decimal format');
    return decimalValue;
  }
  
  // Check if it's HH:MM format (contains colon)
  if (trimmed.includes(':')) {
    return parseHHMMToDecimalHours(trimmed);
  }
  
  // If it's just a number, treat as decimal hours
  const decimalValue = parseFloat(trimmed);
  if (isNaN(decimalValue)) throw new Error('Invalid number format');
  return decimalValue;
};

// Extract abbreviation from worktype name (e.g., "(IT) Diverse IT" -> "(IT)")
const extractAbbreviation = (workTypeName) => {
  const match = workTypeName.match(/^\(([^)]+)\)/);
  return match ? `(${match[1]})` : workTypeName;
};

// Ferieafrunding - afrund til nærmeste halve eller hele arbejdsdag
const applyVacationRounding = (hours, timecodeName) => {
  // Check if timecode contains "FER" (case insensitive)
  if (timecodeName && timecodeName.toUpperCase().includes('FER')) {
    // Round to either 3.75 (half day) or 7.5 (full day)
    // Midpoint at 5.625 hours = (3.75 + 7.5) / 2
    return hours <= 5.625 ? 3.75 : 7.5;
  }
  return hours; // No rounding for non-vacation codes
};

export default function EventFormModal({ isOpen, onClose, onSave, start, end, projects, initialData, onDelete, selectedSlotDate, currentView, modalPosition, onTimeChange, setModalPosition, getModalPositionStyle, onNavigate, currentDate, billableAutoFill, readOnly = false }) {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [hours, setHours] = useState('');
  const [projectId, setProjectId] = useState('');
  const [timecodeId, setTimecodeId] = useState('');
  const [title, setTitle] = useState('');
  const [billingtype, setBillingType] = useState('');
  const [projectTimecodes, setProjectTimecodes] = useState([]);
  // 'idle' | 'loading' | 'ready' | 'error' for the work-type fetch, and a counter
  // the Retry button bumps to run it again.
  const [timecodeStatus, setTimecodeStatus] = useState('idle');
  const [timecodeRetry, setTimecodeRetry] = useState(0);
  const [endTime, setEndTime] = useState('');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [sendToCapLegal, setSendToCapLegal] = useState(true);
  const [vacationRoundingMessage, setVacationRoundingMessage] = useState('');

  // Tracks which entry we've already initialized form fields for, to prevent
  // onTimeChange-triggered re-renders from resetting user-typed notes/fields.
  const initializedLogIdRef = React.useRef(null);

  // State for tracking screen size for responsive modal positioning
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.classList.add('modal-open');
      return () => {
        document.body.classList.remove('modal-open');
      };
    }
  }, [isOpen, isMobile]);

  const readonly = readOnly || initialData?.approval_status === 'Approved';

  // Outlook Sync entries live in the same Timelogs module and can have their
  // Matter/Work Type filled in after the fact ("converted" into a normal entry).
  const isOutlookSync = !!initialData?.outlookSync;
  const canEditProject = !readonly && isOutlookSync;

  const resetForm = () => {
    setDate('');
    setStartTime('');
    setHours('');
    setProjectId('');
    setTimecodeId('');
    setTitle('');
    setBillingType('');
    setProjectTimecodes([]);
    setEndTime('');
    setSendToCapLegal(true);
    setVacationRoundingMessage('');
    initializedLogIdRef.current = null;
  };

  useEffect(() => {
    if (hasSubmitted && Object.keys(errors).length === 0) {
      setHasSubmitted(false);
    }
  }, [errors, hasSubmitted]);

  // Listen for window resize to update mobile state for responsive modal positioning
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update startTime/endTime -> hours
    useEffect(() => {
      if (lastChangedField === 'startTime' || lastChangedField === 'endTime') {
        if (startTime && endTime && date) {
          const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
          // Only a genuine midnight crossing moves to the next day. An end equal
          // to its start is a zero-length log, not 24 hours. See durationRule.js.
          const endMoment = nextDayCorrectedEnd(
            startMoment,
            moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm'),
          );

          const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
          setHours(formatDecimalHoursToHHMM(diffInHours));

          // Notify parent to update event in calendar
          if (onTimeChange) {
            onTimeChange({
              ...(initialData || {}),
              start: startMoment.toDate(),
              end: endMoment.toDate(),
              logId: initialData?.logId || null,
            });
          }
        }
      }
    }, [startTime, endTime, date]); 

  useEffect(() => {
    if (initialData) {
      // Always sync time/date fields so the calendar reflects time drag changes.
      setDate(moment(initialData.start).format('YYYY-MM-DD'));
      setStartTime(moment(initialData.start).format('HH:mm'));
      const durationMs = moment(initialData.end).diff(moment(initialData.start));
      const durationHours = durationMs / 3600000;
      setHours(formatDecimalHoursToHHMM(durationHours));
      setEndTime(moment(initialData.end).format('HH:mm'));

      // Only initialize user-editable fields when opening a different entry.
      // Without this guard, onTimeChange causes the parent to update start/end,
      // which re-fires this effect and overwrites notes the user just typed.
      const logId = initialData.logId;
      if (logId !== initializedLogIdRef.current) {
        initializedLogIdRef.current = logId;
        setProjectId(initialData.projectId || '');
        setTimecodeId(initialData.timecodeId || '');
        setTitle(initialData.title || '');
        setBillingType(initialData.billingtype || '');
        setSendToCapLegal(initialSendToCapLegal(initialData));
      }
    } else if (start && end) {
      setDate(moment(start).format('YYYY-MM-DD'));
      setStartTime(moment(start).format('HH:mm'));
      setEndTime(moment(end).format('HH:mm'));
      const durationMs = moment(end).diff(moment(start));
      const durationHours = durationMs / 3600000; // ms to hours
      setHours(formatDecimalHoursToHHMM(durationHours));
    }
  }, [start, end, initialData]);
  
  useEffect(() => {
    if (!isOpen) return;

    if (readonly) return;

    const handleArrowKeys = (event) => {
      const tag = document.activeElement.tagName;
      const isEditable = (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        document.activeElement.isContentEditable
      );
      if (isEditable) return;

      if (!readonly && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const diff = endMoment.diff(startMoment, 'minutes');
        if (diff <= 0) return;

        const delta = event.key === 'ArrowUp' ? -15 : 15;

        if (event.shiftKey) {
          // Shift + Arrow: move only end time
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if new duration would exceed 23 hours
          const newDurationHours = newEnd.diff(startMoment, 'minutes') / 60;
          // Prevent end before start, after day end, or duration over 23 hours
          if (
            newEnd.isSameOrBefore(startMoment) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            newDurationHours > 23
          ) {
            return;
          }
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('endTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: startMoment.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: startMoment.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        } else {
          // Arrow: move both start and end
          const newStart = startMoment.clone().add(delta, 'minutes');
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if duration would exceed 23 hours (should remain same, but double-check)
          const durationHours = newEnd.diff(newStart, 'minutes') / 60;
          if (
            newStart.isBefore(moment(date).startOf('day')) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            durationHours > 23
          ) {
            return;
          }
          setStartTime(newStart.format('HH:mm'));
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('startTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: newStart.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: newStart.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        }
      }
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && (currentView === 'week' || currentView === 'work_week' || currentView === 'three_day')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const dayOfWeek = startMoment.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday

        // Week view: block left on Monday, right on Sunday
        if (currentView === 'week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 0) return;
        }
        // Work week: block left on Monday, right on Friday
        if (currentView === 'work_week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 5) return;
          // Also block moving to weekend
          if (event.key === 'ArrowRight' && dayOfWeek >= 5) return;
          if (event.key === 'ArrowLeft' && dayOfWeek <= 1) return;
        }

        // Calculate new start/end
        const deltaDays = event.key === 'ArrowLeft' ? -1 : 1;
        const newStart = startMoment.clone().add(deltaDays, 'days');
        const newEnd = endMoment.clone().add(deltaDays, 'days');

        // For work_week, prevent moving to Sat/Sun
        if (currentView === 'work_week') {
          const newDay = newStart.day();
          if (newDay === 0 || newDay === 6) return;
        }


        setDate(newStart.format('YYYY-MM-DD'));
        setStartTime(newStart.format('HH:mm'));
        setEndTime(newEnd.format('HH:mm'));
        setLastChangedField('startTime');

        // For three_day view, check if we need to navigate the calendar
        let shouldUpdateModalPosition = true;
        if (currentView === 'three_day' && onNavigate && currentDate) {
          // Calculate current 3-day range shown in calendar
          const calendarStart = moment(currentDate).startOf('day');
          const calendarEnd = moment(calendarStart).add(2, 'days').endOf('day');
          
          // Check if new date is outside current 3-day range
          if (newStart.isBefore(calendarStart, 'day') || newStart.isAfter(calendarEnd, 'day')) {
            // Navigate calendar to show the new date
            onNavigate(newStart.toDate());
            // Delay modal position update with the new calculated range
            shouldUpdateModalPosition = false;
            setTimeout(() => {
              // Calculate the new 3-day range that will be shown
              const newRangeStart = newStart.startOf('day');
              const dayIndex = newStart.diff(newRangeStart, 'days'); // This will be 0
              
              // Position modal based on the new range
              let newPositionStyle = { right: '20px' };
              if (dayIndex === 2) {
                newPositionStyle = { left: '20px' };
              }
              
              setModalPosition({
                ...newPositionStyle,
                position: 'fixed',
                top: '80px',
                zIndex: 2600,
              });
            }, 50);
          }
        }

        // Update modal position so it doesn't cover the event (for views without navigation)
        if (shouldUpdateModalPosition) {
          setModalPosition(getModalPositionStyle(newStart.toDate(), currentView));
        }

        // Notify parent
        if (onTimeChange && initialData) {
          onTimeChange({
            ...initialData,
            start: newStart.toDate(),
            end: newEnd.toDate(),
          });
        }
        if (onTimeChange && !initialData && start && end) {
          onTimeChange({
            start: newStart.toDate(),
            end: newEnd.toDate(),
            logId: null,
          });
        }
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, date, startTime, endTime, readonly, onTimeChange, initialData, start, end]);

  useEffect(() => {
    // A response that arrives after the matter changed must not overwrite the
    // status or options of the newer request.
    let cancelled = false;
    if (projectId) {
      setTimecodeStatus('loading');
      API.fetchTasksByProject(projectId)
        .then((data) => {
          if (cancelled) return;
          setTimecodeStatus(data.success ? 'ready' : 'error');
          if (data.success) {
            console.log('✅ Fetched tasks for project:', projectId, data.data);
            setProjectTimecodes(data.data);
            if (timecodeId) {
              const byId = data.data.find(tc => String(tc.id) === String(timecodeId));
              if (!byId) {
                // ID mismatch (possible numeric precision loss) — try matching by name
                const timecodeName = initialData?.timecodeName;
                const byName = timecodeName && data.data.find(tc => tc.name === timecodeName);
                if (byName) {
                  console.log('⚠️ timecodeId not found by ID, matched by name:', timecodeName, '→', byName.id);
                  setTimecodeId(byName.id);
                } else {
                  console.warn('❌ timecodeId not found by ID or name. timecodeId:', timecodeId, 'timecodeName:', timecodeName);
                  setTimecodeId('');
                }
              }
            }
          } else {
            setProjectTimecodes([]);
            setTimecodeId('');
          }
        })
        .catch(() => {
          if (cancelled) return;
          console.log('❌ Error fetching tasks');
          setTimecodeStatus('error');
          setProjectTimecodes([]);
          setTimecodeId('');
        });
    } else {
      setTimecodeStatus('idle');
      setProjectTimecodes([]);
    }
    return () => { cancelled = true; };
  }, [projectId, timecodeId, projects, timecodeRetry]);

  // Auto-set billing type when a timecode is selected.
  //
  // A matter belonging to the firm itself is never billable, whatever the work type
  // says, so it answers as soon as the matter is picked instead of waiting for the
  // work type. The server applies the same rule when it stores the log, so what is
  // shown here is what CRM holds. An existing internal log stored as Billable before
  // this shipped therefore reads Non Billable, and saving it corrects the record.
  useEffect(() => {
    const selectedMatter = projects.find(p => String(p.id) === String(projectId));
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    setBillingType(billingTypeForMatter(selectedMatter, selected));
  }, [timecodeId, projectTimecodes, projectId, projects]);

  // Check for vacation rounding when hours or timecode changes
  useEffect(() => {
    if (hours && timecodeId) {
      try {
        const currentHours = parseHHMMToDecimalHours(hours);
        const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
        const timecodeName = selectedTimecode?.name || '';
        
        if (timecodeName.toUpperCase().includes('FER')) {
          const roundedHours = applyVacationRounding(currentHours, timecodeName);
          if (roundedHours !== currentHours) {
            const dayType = roundedHours === 3.75 ? 'half day' : 'full day';
            setVacationRoundingMessage(`Holiday hours will be rounded to ${roundedHours} hours (${dayType})`);
          } else {
            setVacationRoundingMessage('');
          }
        } else {
          setVacationRoundingMessage('');
        }
      } catch (e) {
        setVacationRoundingMessage('');
      }
    } else {
      setVacationRoundingMessage('');
    }
  }, [hours, timecodeId, projectTimecodes]);

  // Auto-fill notes based on worktype selection
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      // Skip auto-fill if this is an existing entry with the same timecode (preserve Zoho Projects notes)
      if (initialData && initialData.timecodeId === timecodeId && initialData.title && initialData.title.trim()) {
        return; // Don't overwrite original notes from Zoho Projects
      }

      if (selected.billingtype === 'Non Billable' || billableAutoFill) {
        // Set notes to worktype abbreviation for non-billable tasks
        const workTypeName = selected.name || '';
        const abbreviation = extractAbbreviation(workTypeName);
        const currentTitle = title;
        
        // Check if current title contains "|" separator
        if (currentTitle.includes(' | ')) {
          // Replace only the worktype part (before "|"), keep user text after "|"
          const userText = currentTitle.split(' | ').slice(1).join(' | ');
          setTitle(`${abbreviation} | ${userText}`);
        } else {
          // For Non-Billable, preserve user text and just add abbreviation prefix
          // Check if currentTitle is any worktype abbreviation pattern (starts with "(" and ends with ")")
          const isWorktypeAbbreviation = /^\([^)]+\)$/.test(currentTitle.trim());
          
          if (currentTitle.trim() && !isWorktypeAbbreviation) {
            setTitle(`${abbreviation} | ${currentTitle}`);
          } else {
            setTitle(abbreviation);
          }
        }
      } else {
        // For billable tasks, preserve user-entered text
        // Only clear if it's clearly auto-generated (contains specific patterns) AND empty of user content
        const isAutoGenerated = title.includes(' | ') && title.split(' | ').length === 2 && !title.split(' | ')[1].trim();
        if (isAutoGenerated || !title.trim()) {
          setTitle('');
        }
        // If user has written custom text, always preserve it
      }
    }
  }, [timecodeId, projectTimecodes, initialData, billableAutoFill]);

  useEffect(() => {
    validateForm();
  }, [date, startTime, hours, projectId, timecodeId, title]);

  // Tilføj event listener for Escape-tasten
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges, readonly]);

  const validateForm = () => {
    const newErrors = {};

    if (!date) newErrors.date = "Please select a date";
    if (!startTime) newErrors.startTime = "Please enter a start time";
    if (!hours) newErrors.hours = "Please enter hours";
    else {
      // Support both HH:MM and decimal formats
      const hhmmPattern = /^\d{1,2}:\d{2}$/;
      const decimalPattern = /^\d*[,.]?\d+$/;
      
      if (!hhmmPattern.test(hours) && !decimalPattern.test(hours)) {
        newErrors.hours = "Use format like 1:30, 0,1 or 1.5";
      } else {
        try {
          const numericHours = parseFlexibleTimeToDecimalHours(hours);
          if (numericHours <= 0) newErrors.hours = "Hours must be greater than zero";
          if (numericHours > 23) newErrors.hours = "Hours must be less than 23";
        } catch (e) {
          newErrors.hours = "Invalid time format";
        }
      }
    }

    if (!projectId) newErrors.projectId = "Please select a matter";
    if (!title.trim()) newErrors.title = "Please enter notes";

    if (!timecodeId) newErrors.timecodeId = "Please select a worktype";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const isValid = validateForm();
    if (!isValid) {
      console.log('Form validation failed:', errors);
      return;
    }

    const startDate = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
    let floatHours;
    try {
      floatHours = parseFlexibleTimeToDecimalHours(hours);
    } catch (e) {
      setErrors(prev => ({ ...prev, hours: 'Invalid time format' }));
      return;
    }
    if (isNaN(floatHours) || floatHours <= 0) {
      setErrors(prev => ({ ...prev, hours: 'Invalid hours entered' }));
      return;
    }

    // Apply vacation rounding for FER timecodes
    const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
    const timecodeName = selectedTimecode?.name || '';
    floatHours = applyVacationRounding(floatHours, timecodeName);

    const durationMs = floatHours * 3600000; // Convert hours to ms
    const endDate = new Date(startDate.getTime() + durationMs);
    
    // Log overnight event detection for debugging
    if (endDate.getDate() !== startDate.getDate()) {
      console.log('📅 Overnight event detected:', {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        hours: floatHours
      });
    }

    if (isOutlookSync && initialData && projectId && timecodeId) {
      // This is a conversion from an Outlook Sync entry into a normal timelog
      onSave({
        title,
        start: startDate,
        end: endDate,
        projectId,
        timecodeId,
        billingtype,
        logId: initialData.logId, // Keep the log ID for the update
        projectName: projectOptions.find(p => p.value === projectId)?.label || '',
        timecodeName: timecodeOptions.find(tc => tc.value === timecodeId)?.label || '',
        sendToCapLegal,
        convertFromCRM: true, // Flag to indicate this needs special handling
      });
    } else {
      onSave({
        title,
        start: startDate,
        end: endDate,
        projectId,
        timecodeId,
        billingtype,
        logId: initialData?.logId || null,
        projectName: projectOptions.find(p => p.value === projectId)?.label || '',
        timecodeName: timecodeOptions.find(tc => tc.value === timecodeId)?.label || '',
        sendToCapLegal,
      });
    }
    setHasSubmitted(false);
    resetForm();
    onClose();
  };


  const handleDelete = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    onDelete(initialData?.logId, initialData?.projectId, initialData?.timecodeId);
    resetForm();
    onClose();
    setShowDeleteConfirmModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
  };

  const handleClose = () => {
    if (hasChanges && !readonly) {
      setShowConfirmModal(true);
    } else {
      resetForm();
      onClose();
      setHasSubmitted(false);
      setHasChanges(false);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetForm();
    onClose();
    setHasSubmitted(false);
    setHasChanges(false);
  };

  const handleCancelClose = () => {
    setShowConfirmModal(false);
  };

  // Tjek for ændringer når inputs ændres
  useEffect(() => {
    if (!readonly) {
      setHasChanges(
        projectId !== (initialData?.projectId || '') ||
        timecodeId !== (initialData?.timecodeId || '') ||
        title !== (initialData?.title || '') ||
        sendToCapLegal !== true
      );
    }
  }, [projectId, timecodeId, title, initialData, readonly, sendToCapLegal]);

  if (!isOpen) return null;

  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const timecodeOptions = projectTimecodes.map((tc) => ({
    value: tc.id,
    label: tc.name,
  }));

  // Mobile-first positioning: center on mobile with respect to fixed header/footer
  const mobileStyles = {
    position: 'fixed',
    top: '20px', // Minimal margin from top (header will be 70px + 20px = 90px from top)
    left: '50%',
    transform: 'translateX(-50%)', // Only center horizontally
    zIndex: 2600,
    bottom: '20px', // Minimal margin from bottom (footer will be 90px + 20px = 110px from bottom)
    // Let the modal content scroll if needed
    display: 'flex',
    flexDirection: 'column',
  };

  const finalModalPosition = isMobile ? mobileStyles : modalPosition;

  return (
    <>
      {/* Backdrop overlay for all screen sizes */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 md:bg-transparent"
        style={{ zIndex: 2500 }}
        onClick={handleClose}
      />
        
      <div
        className="bg-white rounded-lg w-full max-w-[88vw] md:w-[400px] h-auto max-h-full md:max-h-[90vh] shadow-xl border border-gray-300 modal-mobile flex flex-col"
        style={finalModalPosition}
        onClick={(e) => e.stopPropagation()}
      >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-3 p-3 md:p-6 pb-0 md:pb-0">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              {readOnly ? 'View entry' : initialData ? 'Edit' : 'New entry'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-3 md:p-6 pt-0 md:pt-0">

          {readOnly && initialData?.approval_status !== 'Approved' && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-800 text-sm font-medium">
                You have read-only access to this employee's timelogs.
              </p>
            </div>
          )}

          {/* Approved Warning Badge */}
          {readonly && (
            <div className="mb-3 p-3 bg-[#d6c08b] border border-[#ccb778] rounded-md">
              <p className="text-white text-sm font-bold">
                If you wish to change this approved registration, please{' '}
                <a 
                  href={(() => {
                    const zuid = new URLSearchParams(window.location.search).get('zuid');
                    const formatDate = (dateStr) => {
                      const date = new Date(dateStr);
                      return date.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric'
                      });
                    };
                    const cleanLogId = (initialData?.logId || '').toString();

                    const subject = `Timelog ${cleanLogId} - ${initialData?.start ? formatDate(initialData.start) : ''}`;
                    const body = `Hello - regarding timelog ${cleanLogId} on ${initialData?.start ? formatDate(initialData.start) : ''} for ${initialData?.projectName || ''} with ${initialData?.timecodeName || ''} I have the following request:

XX

zuid: ${zuid || ''}`;
                    
                    return `mailto:finance@moalemweitemeyer.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  })()}
                  className="underline hover:text-yellow-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact Finance
                </a>.
              </p>
            </div>
          )}

          {/* Date og Hours - side by side on mobile like Start Time/End Time */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Date:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Hours:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => {
                  // Allow numbers, colon, comma, and period for flexible input
                  let val = e.target.value.replace(/[^0-9:,.]/g, '');
                  setHours(val);
                  setLastChangedField('hours');
                }}
                onBlur={() => {
                  // Try to parse and validate the hours input using flexible parser
                  try {
                    const parsed = parseFlexibleTimeToDecimalHours(hours);
                    if (!isNaN(parsed) && parsed > 0 && parsed <= 23) {
                      // Valid: update end time
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = startMoment.clone().add(parsed, 'hours');
                      setEndTime(endMoment.format('HH:mm'));
                      // Notify parent
                      if (onTimeChange) {
                        onTimeChange({
                          ...(initialData || {}),
                          start: startMoment.toDate(),
                          end: endMoment.toDate(),
                          logId: initialData?.logId || null,
                        });
                      }
                    } else {
                      // Invalid: revert to current diff between start and end
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                      if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                        const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                        setHours(formatDecimalHoursToHHMM(diffInHours));
                      } else {
                        setHours('');
                      }
                    }
                  } catch (e) {
                    // Invalid format: revert to current diff between start and end
                    const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                    const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                    if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                      const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                      setHours(formatDecimalHoursToHHMM(diffInHours));
                    } else {
                      setHours('');
                    }
                  }
                }}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
                placeholder="E.g. 1:30 or 0,1 or 1.5"
              />
              {hasSubmitted && errors.hours && <div className="text-red-500 text-sm mt-1">{errors.hours}</div>}
              {vacationRoundingMessage && (
                <div className="text-gray-500 text-xs mt-1 font-medium">
                  {vacationRoundingMessage}
                </div>
              )}
            </div>
          </div>

          {/* Start Time og End Time - side by side on mobile like SeriesModal */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Start Time:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setLastChangedField('startTime');
                }}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.startTime && <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">End Time:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setLastChangedField('endTime');
                }}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.endTime && <div className="text-red-500 text-sm mt-1">{errors.endTime}</div>}
            </div>
          </div>

          {/* Matter som fuld bredde */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">
              Matter:
            </label>
            <Select
              options={projectOptions}
              filterOption={matchesAllTokens}
              value={projectOptions.find(option => option.value === projectId)}
              onChange={(selectedOption) => {
                if (!initialData || canEditProject) {
                  setProjectId(selectedOption?.value || '');
                }
              }}
              isClearable={!initialData || canEditProject}
              isDisabled={!!initialData && !canEditProject}
              placeholder="Search or select a matter..."
              styles={withDarkStyles({
                control: (provided) => ({
                  ...provided,
                  minHeight: '44px',
                  padding: '2px 4px',
                  fontSize: '16px',
                }),
                placeholder: (provided) => ({ ...provided, fontSize: '16px' }),
                input: (provided) => ({ ...provided, fontSize: '16px' }),
                singleValue: (provided) => ({ ...provided, fontSize: '16px' }),
              })}
            />
            {hasSubmitted && errors.projectId && <div className="text-red-500 text-sm mt-1">{errors.projectId}</div>}
          </div>

          {/* Worktype som fuld bredde */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Worktype:</label>
            <Select
              options={timecodeOptions}
              filterOption={matchesAllTokens}
              value={(() => {
                if (!timecodeId) return null;

                // First try to find in loaded options
                const foundOption = timecodeOptions.find(option => option.value === timecodeId);
                if (foundOption) return foundOption;

                // Tasks haven't loaded yet — show name from initialData as fallback
                if (initialData?.timecodeId === timecodeId && initialData?.timecodeName) {
                  return { value: initialData.timecodeId, label: initialData.timecodeName };
                }

                return null;
              })()}
              onChange={(selectedOption) => {
                console.log('Worktype changed:', selectedOption);
                setTimecodeId(selectedOption?.value || '');
              }}
              isClearable
              isLoading={timecodeStatus === 'loading'}
              noOptionsMessage={() => worktypeMenuMessage(timecodeStatus)}
              isDisabled={!projectId || readonly}
              placeholder={projectId ? "Search or select a worktype..." : "Select a matter first..."}
              styles={withDarkStyles({
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '44px',
                  padding: '2px 4px',
                  fontSize: '16px',
                  cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                }),
                placeholder: (provided) => ({ ...provided, fontSize: '16px' }),
                input: (provided) => ({ ...provided, fontSize: '16px' }),
                singleValue: (provided) => ({ ...provided, fontSize: '16px' }),
              })}
            />
            {timecodeStatus === 'error' && (
              <div className="text-red-500 text-sm mt-1" role="alert">
                Could not load worktypes for this matter.{' '}
                <button type="button" className="underline" onClick={() => setTimecodeRetry(n => n + 1)}>
                  Retry
                </button>
              </div>
            )}
            {hasSubmitted && errors.timecodeId && <div className="text-red-500 text-sm mt-1">{errors.timecodeId}</div>}
          </div>
          {billingtype && (
            <div className="mb-2 flex flex-col">
              <label className="text-sm md:text-base font-medium mb-1">Billing Type:</label>
              <input
                type="text"
                value={billingtype}
                disabled
                className="p-2 text-base rounded border border-gray-300 bg-gray-50 text-gray-400 w-full touch-manipulation"
              />
            </div>
          )}

          {/* Notes */}
          {projectId && (
              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Notes:</label>
                <textarea
                  value={title}
                  onChange={(e) => {
                    console.log('Notes changed, isOutlookSync:', isOutlookSync, 'readonly:', readonly);
                    const newValue = e.target.value.trimStart();
                    const selected = projectTimecodes.find(tc => tc.id === timecodeId);

                    // Check if this is a Non-Billable worktype and user is typing after worktype abbreviation
                    if (selected && (selected.billingtype === 'Non Billable' || billableAutoFill) && selected.name) {
                      const workTypeName = selected.name;
                      const abbreviation = extractAbbreviation(workTypeName);

                      // If current value is just the worktype abbreviation and user is adding text
                      if (title === abbreviation && newValue.length > abbreviation.length && !newValue.includes(' | ')) {
                        // Add " | " separator and the new text
                        const userText = newValue.substring(abbreviation.length);
                        setTitle(`${abbreviation} | ${userText}`);
                        return;
                      }
                    }

                    setTitle(newValue);
                  }}
                  className={`p-2 text-base rounded border border-gray-300 resize-none touch-manipulation ${(readonly && !isOutlookSync) ? 'bg-gray-50 text-gray-400' : ''}`}
                  placeholder="Enter notes..."
                  disabled={readonly && !isOutlookSync}
                  maxLength={255}
                  rows={4}
                  style={{ fontSize: '16px' }}
                />
                <div className="text-sm text-gray-600 text-right">
                  {title.length}/255
                </div>
                {hasSubmitted && errors.title && <div className="text-red-500 text-sm mt-1">{errors.title}</div>}
              </div>
          )}

          {/* Send to CapLegal with delay checkbox */}
          <div className="mb-3 flex items-center">
            <input
              type="checkbox"
              id="sendToCapLegal"
              className="mr-3 h-5 w-5 md:h-4 md:w-4 accent-[#ccb778] cursor-pointer touch-manipulation"
              disabled={readonly}
              checked={sendToCapLegal}
              onChange={(e) => setSendToCapLegal(e.target.checked)}
            />
            <label htmlFor="sendToCapLegal" className="cursor-pointer text-sm md:text-base">Send to CapLegal with delay</label>
          </div>

          {/* Created By */}
          {initialData?.createdBy && (
            <div className="mt-2 mb-1 text-xs text-gray-400">
              Created by: {initialData.createdBy}
            </div>
          )}

          </div> {/* End Scrollable Content */}

          {/* Sticky Action Buttons */}
          <div className="border-t border-gray-200 p-3 md:p-6 bg-white rounded-b-lg">
          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
            {!readonly && (
              <>
                <div className="order-2 md:order-1">
                  {initialData && (
                    <button
                      onClick={handleDelete}
                      className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 touch-manipulation font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="order-1 md:order-2">
                  <button
                    onClick={handleSubmit}
                    className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-[#ccb778] text-white rounded hover:bg-[#b49646] disabled:opacity-50 touch-manipulation font-medium"
                    disabled={readonly}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
          </div> {/* End Sticky Action Buttons */}
        </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Unsaved Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal 
        isOpen={showDeleteConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}
